import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog,
  Calculator, FlaskConical, Search, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const SubjectAllocation = () => {
  const { api } = useAuth();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
  ];

  const subjects = [
    { id: 'maths', name: 'Maths', icon: Calculator, color: 'amber' },
    { id: 'english', name: 'English', icon: BookOpen, color: 'pink' },
    { id: 'science', name: 'Science', icon: FlaskConical, color: 'emerald' },
  ];

  const fetchStudents = async () => {
    try {
      const response = await api.get('/v2/admin/users?role=student');
      setStudents(response.data.users);
      setFilteredStudents(response.data.users);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [api]);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredStudents(students.filter(s => 
        s.first_name?.toLowerCase().includes(term) ||
        s.last_name?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const handleSubjectToggle = async (studentId, subjectId, currentSubjects) => {
    setSaving(prev => ({ ...prev, [studentId]: true }));
    
    const hasSubject = currentSubjects.includes(subjectId);
    const newSubjects = hasSubject 
      ? currentSubjects.filter(s => s !== subjectId)
      : [...currentSubjects, subjectId];
    
    try {
      await api.post('/admin/allocate-subjects', {
        student_id: studentId,
        subjects: newSubjects
      });
      
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { ...s, allocated_subjects: newSubjects } : s
      ));
      
      toast.success('Subjects updated');
    } catch (error) {
      toast.error('Failed to update subjects');
    } finally {
      setSaving(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const allocateAllSubjects = async (studentId) => {
    setSaving(prev => ({ ...prev, [studentId]: true }));
    const allSubjects = subjects.map(s => s.id);
    
    try {
      await api.post('/admin/allocate-subjects', {
        student_id: studentId,
        subjects: allSubjects
      });
      
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { ...s, allocated_subjects: allSubjects } : s
      ));
      
      toast.success('All subjects allocated');
    } catch (error) {
      toast.error('Failed to allocate subjects');
    } finally {
      setSaving(prev => ({ ...prev, [studentId]: false }));
    }
  };

  return (
    <ProLayout title="Subject Allocation" sidebarItems={sidebarItems}>
      <div data-testid="subject-allocation">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800">
            <strong>Allocate subjects to students.</strong> Students can only access lessons for subjects they're enrolled in.
          </p>
        </div>

        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-students"
            />
          </div>
        </div>

        {/* Students Table */}
        <div className="pro-card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Year / Key Stage</th>
                    {subjects.map(subj => (
                      <th key={subj.id} className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <subj.icon className={`w-4 h-4 text-${subj.color}-600`} />
                          {subj.name}
                        </div>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan={5 + subjects.length} className="text-center py-8 text-slate-500">
                        No students found
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student) => {
                      const currentSubjects = student.allocated_subjects || [];
                      const isSaving = saving[student.id];
                      
                      return (
                        <tr key={student.id}>
                          <td className="font-medium text-slate-800">
                            {student.first_name} {student.last_name}
                          </td>
                          <td className="text-slate-600">
                            Year {student.year_group} • {student.key_stage?.toUpperCase()}
                          </td>
                          {subjects.map(subj => (
                            <td key={subj.id} className="text-center">
                              <Checkbox
                                checked={currentSubjects.includes(subj.id)}
                                onCheckedChange={() => handleSubjectToggle(student.id, subj.id, currentSubjects)}
                                disabled={isSaving}
                                data-testid={`checkbox-${student.id}-${subj.id}`}
                              />
                            </td>
                          ))}
                          <td>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => allocateAllSubjects(student.id)}
                              disabled={isSaving || currentSubjects.length === subjects.length}
                              data-testid={`allocate-all-${student.id}`}
                            >
                              {isSaving ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  All
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default SubjectAllocation;
